import React, { createContext, useState, useMemo, useCallback } from 'react';
import moment from 'moment';

export const CalendarContext = createContext();

function CalendarProvider({ children }) {
  const VIEW_VALUES = useMemo(
    () => ({
      MONTH: 'month',
      WEEK: 'week',
      DAY: 'day',
    }),
    [],
  );

  const VIEW_TYPES = useMemo(
    () => [
      { label: 'Month', value: VIEW_VALUES.MONTH },
      { label: 'Week', value: VIEW_VALUES.WEEK },
      { label: 'Day', value: VIEW_VALUES.DAY },
    ],
    [VIEW_VALUES],
  );

  // State
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [bigCalendarView, setBigCalendarView] = useState({
    label: 'Month',
    value: VIEW_VALUES.MONTH,
  });

  // Constant calculation
  const hourDifference = useMemo(() => {
    const offset = new Date().getTimezoneOffset();
    return -offset / 60;
  }, []);

  // Navigation callbacks
  const goToNext = useCallback(() => {
    setSelectedDate(prev => {
      const d = moment(prev);
      switch (bigCalendarView.value) {
        case VIEW_VALUES.MONTH:
          return d.add(1, 'month').toDate();
        case VIEW_VALUES.WEEK:
          return d.add(1, 'week').toDate();
        case VIEW_VALUES.DAY:
          return d.add(1, 'day').toDate();
        default:
          return d.toDate();
      }
    });
  }, [bigCalendarView.value, VIEW_VALUES]);

  const goToPrev = useCallback(() => {
    setSelectedDate(prev => {
      const d = moment(prev);
      switch (bigCalendarView.value) {
        case VIEW_VALUES.MONTH:
          return d.subtract(1, 'month').toDate();
        case VIEW_VALUES.WEEK:
          return d.subtract(1, 'week').toDate();
        case VIEW_VALUES.DAY:
          return d.subtract(1, 'day').toDate();
        default:
          return d.toDate();
      }
    });
  }, [bigCalendarView.value, VIEW_VALUES]);

  const goToToday = useCallback(() => {
    setSelectedDate(new Date());
  }, []);

  // Display string generator
  const showDateBasedOnView = useCallback(() => {
    const d = moment(selectedDate);

    switch (bigCalendarView.value) {
      case VIEW_VALUES.MONTH:
        return d.format('MMMM YYYY');

      case VIEW_VALUES.WEEK: {
        const start = d.clone().startOf('week');
        const end = d.clone().endOf('week');
        if (start.month() === end.month()) {
          return `${start.format('DD')} - ${end.format('DD MMMM YYYY')}`;
        }
        return `${start.format('DD MMMM')} - ${end.format('DD MMMM YYYY')}`;
      }

      case VIEW_VALUES.DAY:
        return d.format('DD MMMM YYYY');

      default:
        return d.format('MMMM YYYY');
    }
  }, [selectedDate, bigCalendarView.value, VIEW_VALUES]);

  // Memoize the context value
  const providerValue = useMemo(
    () => ({
      selectedDate,
      setSelectedDate,
      bigCalendarView,
      setBigCalendarView,
      goToNext,
      goToPrev,
      goToToday,
      showDateBasedOnView,
      VIEW_VALUES,
      VIEW_TYPES,
      hourDifference,
    }),
    [
      selectedDate,
      bigCalendarView,
      goToNext,
      goToPrev,
      goToToday,
      showDateBasedOnView,
      VIEW_VALUES,
      VIEW_TYPES,
      hourDifference,
      setSelectedDate,
    ],
  );

  return (
    <CalendarContext.Provider value={providerValue}>
      {children}
    </CalendarContext.Provider>
  );
}

export default CalendarProvider;
